import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID, SecurityContext } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { ClaimsService } from 'src/app/core/services/claims.service';
import { EventService } from 'src/app/core/services/event.service';
import { EventTypes } from 'src/app/core/models/events.model';

@Component({
  selector: 'app-share-invite-modal',
  templateUrl: './share-invite-modal.component.html',
  styleUrls: ['./share-invite-modal.component.scss']
})
export class ShareInviteModalComponent {
  private platforms: any = {};
  private currentClaimId: string | null = null;
  private currentLang: string = 'pt';

  hasSharing = false;
  modalTitle = '';
  modalDescription: SafeHtml = '';
  modalButton = '';

  constructor(
    public dialogRef: MatDialogRef<ShareInviteModalComponent>,
    private translate: TranslateService,
    private claimsService: ClaimsService,
    private eventService: EventService,
    private sanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.currentLang = this.translate.currentLang || this.translate.defaultLang || 'pt';
    this.loadConfig();
  }

  private async loadConfig(): Promise<void> {
    try {
      const claims = await this.claimsService.getMyClaimsWithCase(1, 0);

      if (!claims?.length) {
        this.close();
        return;
      }

      this.currentClaimId = claims[0].id;

      const { data } = await this.claimsService.getCaseById(claims[0].case_id);

      const config: any = data?.configuration || {};
      const sharingConfig = config?.sharing;

      if (!sharingConfig || sharingConfig.enabled === false) {
        this.close();
        return;
      }

      this.platforms = sharingConfig.flows?.platforms || {};

      const modalConfig = sharingConfig.modal || {};
      this.modalTitle =
        modalConfig.title !== undefined ? this.getTranslatedText(modalConfig.title) : '';

      const descriptionHtml =
        modalConfig.description !== undefined
          ? this.getTranslatedTextForModal(modalConfig.description)
          : '';

      const sanitized = this.sanitizer.sanitize(SecurityContext.HTML, descriptionHtml) || '';
      this.modalDescription = this.sanitizer.bypassSecurityTrustHtml(sanitized);

      this.modalButton = this.translate.instant('ShareInviteButton');
      this.hasSharing = true;
    } catch {
      this.close();
    }
  }

  private getTranslatedText(textObj: any): string {
    if (!textObj) return '';

    const text =
      typeof textObj === 'string'
        ? textObj
        : textObj[this.currentLang] || textObj['pt'] || textObj['en'] || '';

    if (!text) return '';

    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(text);
    return hasHtmlTags ? this.convertHtmlToWhatsAppFormat(text) : text;
  }

  private getTranslatedTextForModal(textObj: any): string {
    if (!textObj) return '';
    if (typeof textObj === 'string') return textObj;
    return textObj[this.currentLang] || textObj['pt'] || textObj['en'] || '';
  }

  private convertHtmlToWhatsAppFormat(html: string): string {
    if (!html || !isPlatformBrowser(this.platformId)) return html;

    const cleanedHtml = html.replace(/&nbsp;/g, ' ').replace(/\u00A0/g, ' ');
    const tmp = document.createElement('div');
    tmp.innerHTML = cleanedHtml;

    const normalizeInline = (s: string) =>
      s.replace(/[ \t]+\n/g, '\n').replace(/[ \t]{2,}/g, ' ').trim();

    const processNode = (node: Node): string => {
      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').replace(/\u00A0/g, ' ');
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();
        const children = Array.from(el.childNodes).map(processNode).join('');

        switch (tag) {
          case 'strong':
          case 'b': {
            const t = normalizeInline(children);
            return t ? `*${t}*` : '';
          }
          case 'em':
          case 'i': {
            const t = normalizeInline(children);
            return t ? `_${t}_` : '';
          }
          case 's':
          case 'strike':
          case 'del': {
            const t = normalizeInline(children);
            return t ? `~${t}~` : '';
          }
          case 'code': {
            const t = normalizeInline(children);
            return t ? `\`\`\`${t}\`\`\`` : '';
          }
          case 'br':
            return '\n';

          case 'a': {
            const text = normalizeInline(children);
            const href = (el.getAttribute('href') || '').trim();
            if (text && href) return `${text} ${href}`;
            if (href) return href;
            return text;
          }

          case 'p':
          case 'div': {
            const t = children.trim();
            return t ? `${t}\n` : '\n';
          }

          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6': {
            const t = normalizeInline(children);
            return t ? `*${t}*\n` : '\n';
          }

          default:
            return children;
        }
      }

      return '';
    };

    return Array.from(tmp.childNodes)
      .map(processNode)
      .join('')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]+$/gm, '')
      .trim();
  }

  close(): void {
    this.dialogRef.close();
  }

  async onShare(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || !this.hasSharing) return;

    if (this.isMobile()) {
      await this.shareNative();
    } else {
      this.showDesktopMenu();
    }
  }

  private isMobile(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    );
  }

  private buildShareText(): string {
    const whatsapp = this.platforms.whatsapp || {};
    const url = (whatsapp.url || '').trim();
    const msg = (this.getTranslatedText(whatsapp.message) || '').trim();

    if (msg && url) return `${msg}\n\n${url}`;
    return msg || url;
  }

  private buildWhatsappShareUrl(): string | null {
    const text = this.buildShareText().trim();
    if (!text) return null;
    return `https://wa.me/?text=${encodeURIComponent(text)}`;
  }

  private async shareNative(): Promise<void> {
    const text = this.buildShareText().trim();
    if (!text) return;

    if (isPlatformBrowser(this.platformId) && (navigator as any).share) {
      try {
        await (navigator as any).share({ text });
        return;
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          this.copyToClipboard(text);
        }
        return;
      }
    }

    const whatsappShareUrl = this.buildWhatsappShareUrl();
    if (whatsappShareUrl) {
      window.location.href = whatsappShareUrl;
    } else {
      this.copyToClipboard(text);
    }
  }

  private showDesktopMenu(): void {
    const whatsapp = this.platforms.whatsapp || {};
    const copy = this.platforms.copy_link || {};
    const email = this.platforms.email || {};
    const baseUrl = (whatsapp.url || '').trim();

    const copyUrl = ((copy.url || baseUrl) || '').trim();
    const emailUrl = ((email.url || baseUrl) || '').trim();

    const menu = document.createElement('div');
    menu.className = 'desktop-share-menu';
    menu.innerHTML = `
      <div class="share-menu-content">
        <h3>${this.translate.instant('ShareChooseOption')}</h3>
        <button data-action="copy" data-url="${copyUrl}" class="share-option">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
          <span>${this.translate.instant('ShareCopyLink')}</span>
        </button>
        <button data-action="email" data-url="${emailUrl}" class="share-option">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          <span>${this.translate.instant('ShareSendEmail')}</span>
        </button>
        <button data-action="cancel" class="share-option cancel">
          <span>${this.translate.instant('Cancel')}</span>
        </button>
      </div>
    `;

    document.body.appendChild(menu);
    menu.addEventListener('click', (e) => this.handleMenuClick(e, menu));
  }

  private async handleMenuClick(e: Event, menu: HTMLElement): Promise<void> {
    const target = (e.target as HTMLElement).closest('[data-action]') as HTMLElement;
    if (!target) return;

    const action = target.getAttribute('data-action');
    const url = target.getAttribute('data-url') || '';

    if (action === 'cancel') {
      document.body.removeChild(menu);
      return;
    }

    await this.trackShareAction(action || 'unknown', url);

    switch (action) {
      case 'whatsapp': {
        const wa = this.buildWhatsappShareUrl();
        if (wa) window.open(wa, '_blank');
        break;
      }
      case 'copy':
        this.copyToClipboard(url);
        break;
      case 'email':
        this.openEmailClient(url);
        break;
    }

    document.body.removeChild(menu);
  }

  private copyToClipboard(text: string): void {
    if (!isPlatformBrowser(this.platformId) || !text) return;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => this.showNotification(this.translate.instant('ShareLinkCopied')))
        .catch(() => this.copyToClipboardFallback(text));
    } else {
      this.copyToClipboardFallback(text);
    }
  }

  private copyToClipboardFallback(text: string): void {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.left = '-999999px';
    document.body.appendChild(ta);
    ta.select();

    try {
      (document as any).execCommand('copy');
      this.showNotification(this.translate.instant('ShareLinkCopied'));
    } catch {
      // ignore
    }

    document.body.removeChild(ta);
  }

  private openEmailClient(url: string): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const email = this.platforms.email || {};
    const whatsapp = this.platforms.whatsapp || {};
    const rawMessage = email.message || whatsapp.message || null;

    const emailMessage = this.getTranslatedText(rawMessage);
    const body = emailMessage
      ? encodeURIComponent(`${emailMessage}\n\n${url}`)
      : encodeURIComponent(url);

    window.location.href = `mailto:?body=${body}`;
  }

  private showNotification(message: string): void {
    const n = document.createElement('div');
    n.className = 'share-notification';
    n.textContent = message;
    document.body.appendChild(n);

    setTimeout(() => {
      n.classList.add('fade-out');
      setTimeout(() => document.body.contains(n) && n.remove(), 300);
    }, 2700);
  }

  private async trackShareAction(platform: string, url: string): Promise<void> {
    if (!this.currentClaimId) return;

    try {
      const eventData: any = {
        claim_id: this.currentClaimId,
        platform,
        url,
        device_type: this.isMobile() ? 'mobile' : 'desktop',
        timestamp: new Date().toISOString(),
        date: new Date().toLocaleDateString('pt-BR'),
        time: new Date().toLocaleTimeString('pt-BR')
      };

      if (isPlatformBrowser(this.platformId)) {
        eventData.screen = { width: screen.width, height: screen.height };
        eventData.viewport = { width: innerWidth, height: innerHeight };
      }

      await this.eventService.pushNew(EventTypes.SHARE_INVITE_CLICK, eventData);
    } catch {
      // silent
    }
  }
}